import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  DatePicker,
  InputNumber,
  Switch,
  Divider,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import { apiErrorMessage, apiFieldErrors } from '@/utils/apiError';
import { COHORT_PAGE_SIZE, loadAllCohortSlugs } from '@/pages/custom-ads/slotTargeting';
import {
  cleanPopupTargeting,
  describePopupTargeting,
  segmentOptions,
  type PopupTargetingFields,
} from './popupTargeting';
import dayjs from 'dayjs';
import type { FormRule } from 'antd';

const { Title, Text } = Typography;

// Enums matching backend
const DeeplinkType = {
  PRODUCT: 'product',
  SCREEN: 'screen',
  URL: 'url',
  EVENT: 'event',
  OFFER: 'offer',
  NEWS: 'news',
} as const;

const DeeplinkStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SCHEDULED: 'scheduled',
  EXPIRED: 'expired',
} as const;

const DeeplinkPlacement = {
  HOME_BANNER: 'home_banner',
  HOME_TILE: 'home_tile',
  STORE_BANNER: 'store_banner',
  POPUP: 'popup',
  SIDE_PANEL: 'side_panel',
} as const;

interface Deeplink {
  id: string;
  name: string;
  displayName?: string;
  type: (typeof DeeplinkType)[keyof typeof DeeplinkType];
  placement: (typeof DeeplinkPlacement)[keyof typeof DeeplinkPlacement];
  targetData?: string;
  buttonText?: string;
  label?: string;
  imageUrl?: string;
  webpUrl?: string;
  status: (typeof DeeplinkStatus)[keyof typeof DeeplinkStatus];
  platform?: string;
  sortOrder: number;
  minBuildVersion?: number;
  maxBuildVersion?: number;
  startDate?: string;
  endDate?: string;
  displayStartTime?: string;
  displayEndTime?: string;
  includedCountries?: string[];
  excludedCountries?: string[];
  maxImpressionsPerDay?: number;
  maxImpressionsTotalPerUser?: number;
  minPlayerLevel?: number;
  maxPlayerLevel?: number;
  targetUserSegments?: string[];
  sticky?: boolean;
  analyticsEventName?: string;
  metadata?: {
    excludePayers?: boolean;
    hideAfterPurchase?: boolean;
    experiment?: { key: string; variant: string };
    [k: string]: unknown;
  } | null;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  scheduled: 'blue',
  expired: 'red',
};

const typeLabels: Record<string, string> = {
  product: 'Product',
  screen: 'Screen',
  url: 'URL',
  event: 'Event',
  offer: 'Offer',
  news: 'News',
};

const placementLabels: Record<string, string> = {
  home_banner: 'Home Banner',
  home_tile: 'Home Tile',
  store_banner: 'Store Banner',
  popup: 'Popup',
  side_panel: 'Side Panel',
};

// The game compares min/maxBuildVersion with its integer build number. Shipped
// builds send their app version ("0.2"), which the server counts as build 1,
// so an app version such as 0.4 is rejected here (the API answers 422 for it).
const buildVersionRules: FormRule[] = [
  {
    type: 'integer',
    min: 1,
    message: 'Enter a whole build number (e.g. 12), not an app version like 0.4, or leave it empty',
  },
];

const maxBuildVersionRules: FormRule[] = [
  ...buildVersionRules,
  ({ getFieldValue }) => ({
    validator(_, value: number | null | undefined) {
      const min = getFieldValue('minBuildVersion') as number | null | undefined;
      if (typeof value !== 'number' || typeof min !== 'number' || value >= min) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('Max Build Version must not be below Min Build Version'));
    },
  }),
];

const playerLevelRules: FormRule[] = [
  { type: 'integer', min: 1, message: 'Enter a whole level (1 or more), or leave it empty' },
];

/** The experiments a popup row can be limited to (`GET /admin/experiments`). */
interface ExperimentOption {
  key: string;
  status: string;
  variants: string[];
}

// A rule needs both the experiment and its variant, or neither.
const experimentVariantRules: FormRule[] = [
  ({ getFieldValue }) => ({
    validator(_, value: string | null | undefined) {
      const key = getFieldValue(['metadata', 'experiment', 'key']) as string | null | undefined;
      if (Boolean(key) === Boolean(value)) return Promise.resolve();
      return Promise.reject(new Error('Set both the experiment and its variant, or neither.'));
    },
  }),
];

const hideAfterPurchaseOptions = [
  { value: true, label: 'Hide after this SKU is bought' },
  { value: false, label: 'Keep showing' },
];

const impressionCapRules: FormRule[] = [
  { type: 'integer', min: 1, message: 'Enter a whole number (1 or more), or leave it empty' },
];

const DeeplinksPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [deeplinks, setDeeplinks] = useState<Deeplink[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  // Cohort slugs and experiments for the targeting pickers; each stays empty if its call fails.
  const [cohortSlugs, setCohortSlugs] = useState<string[]>([]);
  const [experiments, setExperiments] = useState<ExperimentOption[]>([]);
  const experimentKey = Form.useWatch(['metadata', 'experiment', 'key'], form) as
    | string
    | undefined;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getDeeplinks();
      // Backend returns { data: [...], meta: {...} }
      const data = res.data?.data || res.data?.items || res.data;
      setDeeplinks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch deeplinks', error);
      message.error('Failed to load deeplinks');
      setDeeplinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Loaded once. A failure only costs the cohort slugs: the four spender
  // segments are still offered and the page keeps working.
  useEffect(() => {
    let cancelled = false;
    loadAllCohortSlugs((offset) =>
      adminApi.getCohorts({ limit: COHORT_PAGE_SIZE, offset }).then((res) => res.data)
    ).then((slugs) => {
      if (!cancelled) setCohortSlugs(slugs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getExperiments()
      .then((res) => {
        const rows: Array<{ key?: unknown; status?: unknown; variants?: unknown }> = Array.isArray(
          res.data
        )
          ? res.data
          : [];
        const options = rows
          .filter((row) => typeof row.key === 'string' && row.key !== '')
          .map((row) => ({
            key: row.key as string,
            status: typeof row.status === 'string' ? row.status : 'unknown',
            variants: (Array.isArray(row.variants) ? row.variants : [])
              .map((variant) => (variant as { key?: unknown } | null)?.key)
              .filter((key): key is string => typeof key === 'string' && key !== ''),
          }));
        if (!cancelled) setExperiments(options);
      })
      .catch((error) => {
        console.error('Failed to fetch experiments for popup targeting', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      // Convert dates to ISO strings
      const payload = cleanPopupTargeting<Record<string, unknown> & PopupTargetingFields>(
        {
          ...values,
          startDate: values.startDate ? (values.startDate as dayjs.Dayjs).toISOString() : undefined,
          endDate: values.endDate ? (values.endDate as dayjs.Dayjs).toISOString() : undefined,
        },
        Boolean(editingId)
      );

      if (editingId) {
        await adminApi.updateDeeplink(editingId, payload);
        message.success('Deeplink updated');
      } else {
        await adminApi.createDeeplink(payload);
        message.success('Deeplink created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save deeplink', error);
      // Put each rejected field's reason under that field, and the server's
      // reason in the toast, instead of only "Failed to save deeplink".
      const formFields = new Set(Object.keys(form.getFieldsValue()));
      const fieldErrors = Object.entries(apiFieldErrors(error)).filter(([name]) =>
        formFields.has(name)
      );
      if (fieldErrors.length) {
        form.setFields(fieldErrors.map(([name, msg]) => ({ name, errors: [msg] })));
      }
      message.error(`Failed to save deeplink: ${apiErrorMessage(error, 'unknown error')}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: Deeplink) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      startDate: record.startDate ? dayjs(record.startDate) : undefined,
      endDate: record.endDate ? dayjs(record.endDate) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteDeeplink(id);
      message.success('Deeplink deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete deeplink', error);
      message.error(`Failed to delete deeplink: ${apiErrorMessage(error, 'unknown error')}`);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await adminApi.activateDeeplink(id);
      message.success('Deeplink activated');
      fetchData();
    } catch (error) {
      console.error('Failed to activate deeplink', error);
      message.error(`Failed to activate deeplink: ${apiErrorMessage(error, 'unknown error')}`);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await adminApi.deactivateDeeplink(id);
      message.success('Deeplink deactivated');
      fetchData();
    } catch (error) {
      console.error('Failed to deactivate deeplink', error);
      message.error(`Failed to deactivate deeplink: ${apiErrorMessage(error, 'unknown error')}`);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Deeplink) => (
        <div>
          <Text strong>{name}</Text>
          {record.displayName && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.displayName}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{typeLabels[type] || type}</Tag>,
    },
    {
      title: 'Placement',
      dataIndex: 'placement',
      key: 'placement',
      render: (placement: string) => placementLabels[placement] || placement,
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string | null) => platform || 'All',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Targeting',
      key: 'targeting',
      width: 220,
      ellipsis: { showTitle: false },
      render: (_: unknown, record: Deeplink) => {
        const text = describePopupTargeting(record);
        return (
          <Tooltip title={text} placement="topLeft">
            {text}
          </Tooltip>
        );
      },
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Deeplink) => (
        <Space>
          {record.status === 'active' ? (
            <Button
              icon={<PauseCircleOutlined />}
              size="small"
              onClick={() => handleDeactivate(record.id)}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              icon={<PlayCircleOutlined />}
              size="small"
              type="primary"
              onClick={() => handleActivate(record.id)}
            >
              Activate
            </Button>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this deeplink?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <LinkOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Deeplinks
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Create Deeplink
          </Button>
        }
      >
        <Table
          dataSource={deeplinks}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Deeplink' : 'Create Deeplink'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={800}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="configVersionId"
            label="Config Version"
            extra="Leave empty to show this to every build. Pick a config version to limit it to the app versions mapped to that version."
          >
            <ConfigVersionSelect style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Enter deeplink name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="displayName" label="Display Name">
                <Input placeholder="Optional display name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {Object.entries(DeeplinkType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {typeLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="placement"
                label="Placement"
                initialValue={DeeplinkPlacement.HOME_BANNER}
              >
                <Select placeholder="Select placement">
                  {Object.entries(DeeplinkPlacement).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {placementLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="platform" label="Platform">
                <Select placeholder="Select platform" allowClear>
                  <Select.Option value="android">Android</Select.Option>
                  <Select.Option value="ios">iOS</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Target Configuration</Divider>

          <Form.Item name="targetData" label="Target Data">
            <Input placeholder="Product ID, screen name, or URL" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="buttonText" label="Button Text">
                <Input placeholder="e.g., Shop Now" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="label" label="Label">
                <Input placeholder="Optional label" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="imageUrl" label="Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="webpUrl" label="WebP URL">
                <Input placeholder="https://... (WebP format)" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Scheduling</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue={DeeplinkStatus.INACTIVE}>
                <Select>
                  {Object.entries(DeeplinkStatus).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="startDate" label="Start Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="endDate" label="End Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Display Settings</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="Sort Order"
                initialValue={0}
                rules={[{ type: 'integer', min: 0, message: 'Enter a whole number, 0 or more' }]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              {/* No min/precision props: InputNumber would silently clamp or round 0.4
                  instead of letting the rule explain why it is wrong. */}
              <Form.Item name="minBuildVersion" label="Min Build Version" rules={buildVersionRules}>
                <InputNumber style={{ width: '100%' }} placeholder="Empty = every build" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxBuildVersion"
                label="Max Build Version"
                dependencies={['minBuildVersion']}
                rules={maxBuildVersionRules}
              >
                <InputNumber style={{ width: '100%' }} placeholder="Empty = every build" />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 16 }}>
            Build version is the game&apos;s whole build number, not its app version. Current builds
            count as build 1, so any Min Build Version above 1 hides this deeplink from them. Leave
            both empty to show it to every build; to target an app version such as 0.4, pick a
            Config Version above instead.
          </Text>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="minPlayerLevel" label="Min Player Level" rules={playerLevelRules}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxPlayerLevel" label="Max Player Level" rules={playerLevelRules}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sticky" label="Sticky" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 16 }}>
            Players outside this level range don&apos;t get this popup. Older builds of this page
            saved levels that were never checked, so review them on existing rows.
          </Text>

          <Divider orientation="left">Targeting</Divider>

          <Form.Item
            name="targetUserSegments"
            label="Segments"
            extra="The player must match one. Spender segments: non-payer, minnow, dolphin, whale."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Empty = every player"
              options={segmentOptions(cohortSlugs)}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name={['metadata', 'excludePayers']}
                label="Hide from payers"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name={['metadata', 'hideAfterPurchase']} label="After a purchase">
                <Select
                  allowClear
                  placeholder="Default: only the Legendary card row hides after purchase"
                  options={hideAfterPurchaseOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxImpressionsPerDay"
                label="Max per player per day"
                rules={impressionCapRules}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="Empty = no cap" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxImpressionsTotalPerUser"
                label="Max per player in total"
                rules={impressionCapRules}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="Empty = no cap" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['metadata', 'experiment', 'key']}
                label="Experiment"
                dependencies={[['metadata', 'experiment', 'variant']]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Empty = not part of an experiment"
                  options={experiments.map((experiment) => ({
                    value: experiment.key,
                    label: `${experiment.key} (${experiment.status})`,
                  }))}
                  onChange={() =>
                    form.setFieldValue(['metadata', 'experiment', 'variant'], undefined)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['metadata', 'experiment', 'variant']}
                label="Variant"
                dependencies={[['metadata', 'experiment', 'key']]}
                rules={experimentVariantRules}
              >
                <Select
                  allowClear
                  placeholder="Pick the variant that sees this row"
                  options={(experiments.find((e) => e.key === experimentKey)?.variants ?? []).map(
                    (variant) => ({ value: variant, label: variant })
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 16 }}>
            Targeting is applied per player each time the app asks for the home popup (once per
            launch). A change reaches a player at their next app launch. An experiment row is shown
            only to players in that variant. While the experiment is not running every player counts
            as control: control rows show to everyone and the other variants' rows to nobody. Rows
            with a country rule (shown in the list, e.g. "IN only") go by the player's GA4 location;
            saving here keeps that rule.
          </Text>

          <Divider orientation="left">Analytics</Divider>

          <Form.Item name="analyticsEventName" label="Analytics Event Name">
            <Input placeholder="e.g., deeplink_summer_sale_clicked" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeeplinksPage;
